import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { AvailabilitySlot } from '@his/shared';
import { get, put, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateTime, timeOnly, todayIso } from '@/lib/utils';
import { printDocument } from '@/lib/print';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Field, Input, Select, DatePicker } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import type { SlipData } from './token-slip';
import { PayerPicker } from './PayerPicker';
import { payerChoiceComplete, payerRequest, SELF_PAY, type PayerChoice } from './payer-choice';

interface DoctorOption {
  id: string;
  name: string;
}

export async function printSlip(appointmentId: string, known?: SlipData): Promise<void> {
  const document = await get<{ html: string }>(`/appointments/${known?.appointmentId ?? appointmentId}/slip/print`);
  printDocument(document.html);
}

export function RescheduleDialog({
  appointment,
  onClose,
  onDone,
}: {
  appointment: { id: string; slotAt: string; doctor: { id: string; name: string }; patient: { fullName: string } };
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [doctorId, setDoctorId] = useState(appointment.doctor.id);
  const [date, setDate] = useState(todayIso());
  const [slotAt, setSlotAt] = useState('');
  const [error, setError] = useState<string | null>(null);

  const doctors = useQuery({
    queryKey: ['bookable-doctors'],
    queryFn: async () => (await get<{ data: DoctorOption[] }>('/org/clinicians?kind=bookable&limit=200')).data,
  });
  const availability = useQuery({
    queryKey: ['availability', doctorId, date],
    queryFn: () => get<AvailabilitySlot[]>(`/schedules/availability${qs({ doctorId, date })}`),
    enabled: Boolean(doctorId && date),
  });

  const move = useMutation({
    mutationFn: () =>
      put(`/appointments/${appointment.id}/reschedule`, {
        slotAt,
        ...(doctorId !== appointment.doctor.id ? { doctorId } : {}),
        notifyPatient: true,
      }),
    onSuccess: () => {
      toast.success(tr('opd', 'appointment_moved'));
      onDone();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('opd', 'move_the_appointment')}
      description={tr('opd', 'x_now_x', { fullName: appointment.patient.fullName, slotAt: dateTime(appointment.slotAt) })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('opd', 'keep_it')}
          </Button>
          <Button onClick={() => move.mutate()} loading={move.isPending} disabled={!slotAt}>
            {tr('opd', 'move')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={tr('opd', 'doctor')}>
            <Select value={doctorId} onChange={(e) => { setDoctorId(e.target.value); setSlotAt(''); }}>
              {doctors.data?.map((doctor) => (
                <option key={doctor.id} value={doctor.id}>
                  {doctor.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={tr('opd', 'date')}>
            <DatePicker value={date} onChange={(value) => { setDate(value); setSlotAt(''); }} />
          </Field>
        </div>
        <div className="grid max-h-48 grid-cols-3 gap-1.5 overflow-y-auto rounded-md border p-2 sm:grid-cols-4">
          {availability.data?.map((slot) => (
            <button
              key={slot.startsAt}
              type="button"
              disabled={!slot.available}
              onClick={() => setSlotAt(slot.startsAt)}
              className={`rounded px-2 py-1 text-xs tabular focus-ring ${
                slotAt === slot.startsAt ? 'bg-primary text-primary-foreground' : slot.available ? 'bg-muted hover:bg-accent' : 'cursor-not-allowed bg-muted/40 text-muted-foreground line-through'
              }`}
            >
              {timeOnly(slot.startsAt)}
            </button>
          ))}
          {availability.data?.length === 0 && <p className="col-span-full py-3 text-center text-sm text-muted-foreground">{tr('opd', 'no_clinic_on_this_day')}</p>}
        </div>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}

export function PayerDialog({
  appointment,
  onClose,
  onDone,
}: {
  appointment: { id: string; slotAt: string; patient: { id: string; fullName: string }; payer?: { id: string; name: string } | null; coverageId?: string | null };
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [choice, setChoice] = useState<PayerChoice>(SELF_PAY);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const current = { payerId: appointment.payer?.id ?? null, coverageId: appointment.coverageId ?? null };

  const save = useMutation({
    mutationFn: () => put(`/appointments/${appointment.id}/payer`, { ...payerRequest(choice), reason: reason.trim() }),
    onSuccess: () => {
      toast.success(tr('opd', 'payer_updated'));
      onDone();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('opd', 'who_pays_for_this_visit')}
      description={tr('opd', 'x_currently_x', { fullName: appointment.patient.fullName, payer: appointment.payer?.name ?? tr('opd', 'self_pay') })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('opd', 'cancel')}
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!payerChoiceComplete(choice) || reason.trim().length < 5}>
            {tr('opd', 'save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <PayerPicker
          patientId={appointment.patient.id}
          date={appointment.slotAt.slice(0, 10)}
          at={appointment.slotAt}
          value={choice}
          onChange={setChoice}
          current={current}
        />
        <Field label={tr('opd', 'why_the_payer_is_changing')} required>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={400} />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
